import { useReducer } from 'react'
import { FlyoutId } from '../flyoutId'

type FlyoutState = {
  openFlyoutIds: FlyoutId[]
  // The last non-empty list of open flyout IDs. Stays populated while the
  // closing animation plays so cards remain mounted inside the collapsing wrapper.
  displayedFlyoutIds: FlyoutId[]
  anyFlyoutOpen: boolean
  toggleFlyout: (id: FlyoutId) => void
  closeFlyout: (id: FlyoutId) => void
}

type UseFlyoutStateOptions = {
  initialOpenFlyoutIds?: FlyoutId[]
}

type FlyoutAction = { type: 'toggle'; id: FlyoutId } | { type: 'close'; id: FlyoutId }

type FlyoutReducerState = {
  openIds: FlyoutId[]
  displayedIds: FlyoutId[]
}

const flyoutReducer = (state: FlyoutReducerState, action: FlyoutAction): FlyoutReducerState => {
  const withoutId = state.openIds.filter((flyoutId) => flyoutId !== action.id)
  const nextOpenIds = action.type === 'toggle' && !state.openIds.includes(action.id) ? [...state.openIds, action.id] : withoutId

  return {
    openIds: nextOpenIds,
    // Only update displayed IDs when there is something to show; otherwise
    // keep the previous value so cards stay mounted during the exit animation.
    displayedIds: nextOpenIds.length > 0 ? nextOpenIds : state.displayedIds
  }
}

const createInitialState = (initialOpenFlyoutIds: FlyoutId[]): FlyoutReducerState => ({
  openIds: initialOpenFlyoutIds,
  displayedIds: initialOpenFlyoutIds
})

export const useFlyoutState = ({ initialOpenFlyoutIds = [] }: UseFlyoutStateOptions = {}): FlyoutState => {
  const [{ openIds, displayedIds }, dispatch] = useReducer(flyoutReducer, initialOpenFlyoutIds, createInitialState)

  const toggleFlyout = (id: FlyoutId): void => dispatch({ type: 'toggle', id })
  const closeFlyout = (id: FlyoutId): void => dispatch({ type: 'close', id })

  return {
    openFlyoutIds: openIds,
    displayedFlyoutIds: displayedIds,
    anyFlyoutOpen: openIds.length > 0,
    toggleFlyout,
    closeFlyout
  }
}
